"use client";

import React, { CSSProperties, useState } from "react";
import { cx } from "../../../styled-system/css/cx";
import { Image } from "../../elements/Image";
import { ToggleSwitch } from "../../elements/ToggleSwitch";
import { HotSpot, HotSpotType } from "./components";
import { styles } from "./DamageExplainer.style";

export interface DamageExplainerProps {
  identifier: string;
  backgroundColor: string;
  toggleButtonOnLabel: string;
  toggleButtonOffLabel: string;
  toggleButtonLabel: string;
  backgroundImageDesktop: { url: string; width?: number; height?: number; description?: string; title?: string };
  backgroundImageMobile: { url: string; width?: number; height?: number; description?: string; title?: string };
  hotSpots?: HotSpotType[];
}

export const DamageExplainer = (props: DamageExplainerProps) => {
  const MAX_WIDTH_DESKTOP = 950;
  const MAX_WIDTH_MOBILE = 272;

  const {
    backgroundColor,
    toggleButtonLabel,
    identifier,
    toggleButtonOnLabel,
    toggleButtonOffLabel,
    backgroundImageDesktop,
    backgroundImageMobile,
    hotSpots,
  } = props;

  const [showHotSpots, setShowHotSpots] = useState(true);

  const getBackgroundColor = (color: string): string => {
    switch (color) {
      case "Mist Tint":
        return "#e5f5fd";
      case "White":
        return "#ffffff";
      case "Transparent":
        return "transparent";
      default:
        return color;
    }
  };

  const backgroundWidthDesktop = backgroundImageDesktop.width
    ? backgroundImageDesktop.width < MAX_WIDTH_DESKTOP
      ? `${backgroundImageDesktop.width}px`
      : `${MAX_WIDTH_DESKTOP}px`
    : undefined;

  const backgroundWidthMobile = backgroundImageMobile.width
    ? backgroundImageMobile.width < MAX_WIDTH_MOBILE
      ? `${backgroundImageMobile.width}px`
      : `${MAX_WIDTH_MOBILE}px`
    : undefined;

  const getDataTestId = () => (identifier ? identifier.split(" ").join("-").toLowerCase() : undefined);

  return (
    <div
      className={styles.root()}
      style={{ "--damage-explainer-background": getBackgroundColor(backgroundColor) } as CSSProperties}
      data-testid={getDataTestId()}
    >
      <div className={styles.contentWrapper()}>
        {!!hotSpots?.length && (
          <div className={styles.toggleRow()}>
            <ToggleSwitch
              toggleSwitchLabel={toggleButtonLabel}
              toggleSwitchOnLabel={toggleButtonOnLabel}
              toggleSwitchOffLabel={toggleButtonOffLabel}
              active={showHotSpots}
              onClick={() => setShowHotSpots((value) => !value)}
            />
          </div>
        )}
        <div className={styles.imageContainer()}>
          <div
            className={cx(styles.imageWrapper(), styles.showOnMobile())}
            style={
              {
                "--mobile-image-width": backgroundWidthMobile,
              } as React.CSSProperties
            }
          >
            <Image image={backgroundImageMobile} />
          </div>
          <div
            className={cx(styles.imageWrapper(), styles.showOnDesktop())}
            style={
              {
                "--desktop-image-width": backgroundWidthDesktop,
              } as React.CSSProperties
            }
          >
            <Image image={backgroundImageDesktop} />
          </div>
          {showHotSpots && (
            <div className={styles.hotSpotsWrapper(showHotSpots)}>
              {!!hotSpots?.length &&
                hotSpots.map((hotSpot) => (
                  <React.Fragment key={hotSpot.id}>
                    <HotSpot hotSpot={hotSpot} />
                  </React.Fragment>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
